/**
 * Returns the cookie value.
 * Inspired by: https://developer.mozilla.org/en-US/docs/Web/API/Document/cookie/Simple_document.cookie_framework#Getting_a_cookie
 *
 * @param {string} name - Cookie key
 * @returns {(string|null)}
 */
export function get(name) {
  if (!name) {
    return null;
  }
  return (
    decodeURIComponent(
      document.cookie.replace(
        new RegExp(
          `(?:(?:^|.*;)\\s*${encodeURIComponent(name).replace(
            /[-.+*]/g,
            "\\$&"
          )}\\s*\\=\\s*([^;]*).*$)|^.*$`
        ),
        "$1"
      )
    ) || null
  );
}

/**
 * Write a cookie.
 * Inspired by: https://developer.mozilla.org/en-US/docs/Web/API/Document/cookie/Simple_document.cookie_framework#Writing_a_cookie
 *
 * @param {string} name - Cookie name
 * @param {string} value - Cookie value
 * @param {object} options - Cookie options
 * @param {(number|Infinity|string|Date)} [options.end] - Max age in seconds
 * or Infinity or GTMString or Date, if not,
 * the specified the cookie will expire at the end of the session
 * @param {string} [options.path] - Path
 * @param {string} [options.domain] - Domain
 * @param {boolean} [options.secure] - Transmit only over secure protocol
 * @returns {boolean}
 */
export function set(name, value, { end, path, domain, secure } = {}) {
  if (!name || /^(?:expires|max-age|path|domain|secure)$/i.test(name)) {
    return false;
  }
  let expires = "";
  if (end) {
    switch (end.constructor) {
      case String:
        expires = `; expires=${end}`;
        break;
      case Date:
        expires = `; expires=${end.toUTCString()}`;
        break;
      // Default: Number
      default:
        expires =
          end === Infinity
            ? "; expires=Fri, 31 Dec 9999 23:59:59 GMT"
            : `; max-age=${end}`;
        break;
    }
  }
  document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(
    value
  )}${expires}${domain ? `; domain=${domain}` : ""}${
    path ? `; path=${path}` : ""
  }${secure ? "; secure" : ""}`;
  return true;
}

/**
 * Checks the existance of an cookie.
 * Inspired by: https://developer.mozilla.org/en-US/docs/Web/API/Document/cookie/Simple_document.cookie_framework#Testing_a_cookie
 *
 * @param {string} name - Cookie name
 * @returns {boolean}
 */
export function has(name) {
  if (!name || /^(?:expires|max-age|path|domain|secure)$/i.test(name)) {
    return false;
  }
  return new RegExp(
    `(?:^|;\\s*)${encodeURIComponent(name).replace(/[-.+*]/g, "\\$&")}\\s*\\=`
  ).test(document.cookie);
}

/**
 * Removes a cookie.
 * Inspired by: https://developer.mozilla.org/en-US/docs/Web/API/Document/cookie/Simple_document.cookie_framework#Removing_a_cookie
 *
 * @param {string} name - Cookie name
 * @param {object} options - Cookie options
 * @param {string} [options.path] - Cookie path
 * @param {string} [options.domain] - Cookie domain
 * @returns {boolean}
 */
export function remove(name, { path, domain } = {}) {
  if (!has(name)) {
    return false;
  }
  document.cookie = `${encodeURIComponent(
    name
  )}=; expires=Thu, 01 Jan 1970 00:00:00 GMT${
    domain ? `; domain=${domain}` : ""
  }${path ? `; path=${path}` : ""}`;
  return true;
}
